import { useEffect, useRef, useState } from 'react';
import { ArrowUp } from 'lucide-react';

// ============================================================================
// BACK TO TOP — one shared floating control for every long-form surface.
//
// It appears only after meaningful scrolling (past the first screen and a half),
// returns the window to the top, and is a real <button> with a label, so it is
// reachable by keyboard and announced by a screen reader. Under
// prefers-reduced-motion it neither fades nor animates the scroll: it simply
// appears and jumps. On iPhone Safari it is lifted clear of the browser chrome
// by the bottom safe-area inset, and `liftForNav` lifts it clear of the
// authenticated bottom navigation as well.
// ============================================================================

const LABEL = { en: 'Back to top', es: 'Volver arriba' };

export const BackToTop = ({
  lang = 'en',
  liftForNav = false,
  testId = 'back-to-top',
}) => {
  const [visible, setVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(
    () => typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const ticking = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e) => setReducedMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const read = () => {
      ticking.current = false;
      // meaningful scrolling: past the first screen and a half, and never on a short page
      const threshold = Math.max(window.innerHeight * 1.5, 600);
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      setVisible(scrollable > threshold && window.scrollY > threshold);
    };
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(read);
    };
    read();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const label = LABEL[lang === 'es' ? 'es' : 'en'];
  const gap = liftForNav ? '5.5rem' : '1.5rem';

  return (
    <button
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: reducedMotion ? 'instant' : 'smooth' })}
      aria-label={label}
      title={label}
      tabIndex={visible ? 0 : -1}
      aria-hidden={visible ? 'false' : 'true'}
      data-testid={testId}
      data-visible={visible ? 'true' : 'false'}
      className="group fixed right-5 z-[90] inline-flex items-center justify-center
        w-12 h-12 rounded-full border border-white/12 bg-[#0A0910]/70 backdrop-blur-md
        text-white/65 hover:text-white/95 hover:border-[#D4A843]/50
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A843]/60"
      style={{
        bottom: `calc(${gap} + env(safe-area-inset-bottom, 0px))`,
        right: 'calc(1.25rem + env(safe-area-inset-right, 0px))',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(0.5rem)',
        pointerEvents: visible ? 'auto' : 'none',
        transition: reducedMotion
          ? 'none'
          : 'opacity 240ms ease, transform 240ms ease, color 200ms ease, border-color 200ms ease',
      }}
    >
      <ArrowUp size={18} strokeWidth={1.6} aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </button>
  );
};

export default BackToTop;
